import React from "react";
import { useI18n } from "@/lib/i18n";

// Menú de la boda — organizado por momentos, sin notas técnicas ni texto redundante.
// Cada bloque es un momento del día, con su hora aproximada.
const MOMENTS = [
  {
    id: "benvinguda",
    step: "01",
    time: "Abans de la cerimònia",
    title: "Copa de benvinguda",
    subtitle: "Per rebre-us amb un somriure",
    items: [
      "Llimonada natural",
      "Aigua mineral",
      "Cerveses",
    ],
  },
  {
    id: "obrir-boca",
    step: "02",
    time: "Per obrir boca",
    title: "Snacks salats",
    items: [
      "Fuet en forma de caramels",
      "Xips trufades",
      "Crispetes amb espècies Tandoori",
    ],
  },
  {
    id: "aperitiu",
    step: "03",
    time: "Aperitiu",
    title: "Els nostres 11 bocats",
    subtitle: "Passegen entre vosaltres",
    items: [
      { name: "Pinxo d'esponjós de truita de patata enrabiada" },
      { name: "Escopinyes de Galícia", desc: "en pipeta amb la seva salsa" },
      { name: "Salmorejo amb gamba i pernil ibèric" },
      { name: "Macaró de foie micuit amb espècies" },
      { name: "Taco de salmó marinat", desc: "guacamole i vinagreta de mostassa" },
      { name: "Pop marinat", desc: "amb humus i oli de pebre vermell fumat" },
      { name: "Botifarró de ceba cruixent i confitura de piquillos" },
      { name: "Bacallà en forma de bunyols", desc: "amb maionesa de llima i mel" },
      { name: "Caneló de carn de perol", desc: "salsa de ceps i pols de parmesà" },
      { name: "Ou al plat", desc: "amb sobrassada de porc negre de Mallorca" },
      { name: "Croquetes casolanes de vedella i pollastre", desc: "amb maionesa de rostit" },
    ],
  },
  {
    id: "parades",
    step: "04",
    time: "Paradetes",
    title: "Les 4 estacions",
    subtitle: "Podeu passar quan vulgueu",
    stations: [
      {
        name: "La Formatgeria",
        desc: "Cabra, manxego, gorgonzola, parmesà i petit neu · amb melmelades, figues, préssec i confitura de codony.",
      },
      {
        name: "La Xarcuteria",
        desc: "Pernil ibèric tallat a mà, pa de coca amb tomàquet i oli d'arbequina.",
      },
      {
        name: "Els Plats Cuinats",
        desc: "Fideuà amb fideus rossejats, sípia i fumet de crustacis.",
      },
      {
        name: "Showcooking · Robata",
        desc: "Broquetes de rap, gamba i pop amb cansalada · salsa de romesco i llet de tigre.",
      },
    ],
  },
  {
    id: "barra",
    step: "05",
    time: "Sempre a mà",
    title: "Barra de begudes",
    items: [
      "Refrescs · aigües · sucs de fruita",
      "Cerveses · vermut · còctel de cava",
      "Selecció de vins i caves",
    ],
    finale: {
      title: "I per acabar l'aperitiu",
      subtitle: "Mini còctels digestius en tubs d'assaig",
      items: ["Pinya colada", "San Francisco", "Cava i préssec sanguí"],
    },
  },
  {
    id: "principal",
    step: "06",
    time: "El plat estrella",
    title: "Plat principal",
    items: [
      { name: "Ibèric · escalopa de foie", desc: "poma caramel·litzada i reducció agredolça de codony" },
    ],
    footer: "Amb selecció de pans artesans: rústic · nous · cereals amb pipes.",
  },
  {
    id: "prepostres",
    step: "07",
    time: "Un caprici entre plats",
    title: "Pre-postres",
    items: [
      { name: "Sopeta d'infusió d'herbes aromàtiques", desc: "amb sorbet de mandarina i crispis de passió" },
    ],
  },
  {
    id: "pastis-presentacio",
    step: "08",
    time: "Moment especial",
    title: "Pastís de presentació",
    highlight: true,
    items: [{ name: "Els nuvis porten un pastís amb esplèndides sorpreses" }],
  },
  {
    id: "pastis",
    step: "09",
    time: "El dolç final",
    title: "Pastís",
    items: [
      { name: "Duet", desc: "Saquet de xocolata i el nostre cheesecake" },
    ],
  },
  {
    id: "tancar",
    step: "10",
    time: "Per tancar la nit",
    title: "Cafès i dolços",
    items: [
      "Selecció de cafès aràbics",
      "Selecció de digestius",
      "Gormanderies",
    ],
  },
];

function StepIndex({ step, highlight }) {
  return (
    <div className="flex items-center gap-3 mb-3">
      <span className={`inline-flex items-center justify-center w-9 h-9 rounded-full text-[0.7rem] font-bold tracking-[0.14em] ${highlight ? "bg-[color:var(--ink-wine)] text-white" : "bg-[color:var(--bg-blush)] text-[color:var(--ink-wine)]"}`}>
        {step}
      </span>
      <span className="h-px flex-1 bg-[color:var(--line)]" />
    </div>
  );
}

function Moment({ m }) {
  const renderItem = (it, i) => {
    if (typeof it === "string") {
      return <li key={i} className="text-[color:var(--ink-deep)]">{it}</li>;
    }
    return (
      <li key={i} className="text-[color:var(--ink-deep)]">
        <span className={`${m.highlight ? "font-bold uppercase tracking-[0.14em] text-[color:var(--ink-wine)]" : "font-semibold"}`}>
          {it.name}
        </span>
        {it.desc && <span className="text-[color:var(--ink-soft)] italic"> · {it.desc}</span>}
      </li>
    );
  };
  return (
    <article className="s-card s-card-pad" data-testid={`menu-moment-${m.id}`}>
      <StepIndex step={m.step} highlight={m.highlight} />
      <p className="text-[0.65rem] tracking-[0.32em] uppercase font-bold text-[color:var(--olive-deep)] mb-1">
        {m.time}
      </p>
      <h3 className={`font-script-alt leading-none mb-1 ${m.highlight ? "text-4xl text-[color:var(--ink-wine)]" : "text-3xl text-[color:var(--ink-deep)]"}`}>
        {m.title}
      </h3>
      {m.subtitle && (
        <p className="text-sm italic text-[color:var(--ink-soft)] mb-4">{m.subtitle}</p>
      )}

      {m.items && (
        <ul className={`space-y-2 mt-4 ${m.items.length > 4 ? "sm:columns-2 sm:gap-8 sm:[&>li]:break-inside-avoid" : ""}`}>
          {m.items.map(renderItem)}
        </ul>
      )}

      {m.stations && (
        <div className="grid sm:grid-cols-2 gap-4 mt-4">
          {m.stations.map((s, i) => (
            <div key={i} className="p-4 rounded-xl border border-[color:var(--line)] bg-[color:var(--bg-blush)]/40" data-testid={`menu-station-${s.name.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}>
              <p className="font-script-alt text-2xl text-[color:var(--ink-wine)] leading-none mb-1">{s.name}</p>
              <p className="text-sm text-[color:var(--ink-deep)]">{s.desc}</p>
            </div>
          ))}
        </div>
      )}

      {m.finale && (
        <div className="mt-6 pt-6 border-t border-dashed border-[color:var(--line)] text-center">
          <p className="text-[0.65rem] tracking-[0.28em] uppercase font-bold text-[color:var(--olive-deep)] mb-1">
            {m.finale.title}
          </p>
          <p className="font-script-alt text-2xl text-[color:var(--ink-wine)] leading-none mb-2">
            {m.finale.subtitle}
          </p>
          <p className="text-[color:var(--ink-deep)]">{m.finale.items.join(" · ")}</p>
        </div>
      )}

      {m.footer && (
        <p className="text-xs italic text-[color:var(--ink-soft)] mt-4">{m.footer}</p>
      )}
    </article>
  );
}

export default function MenuPage() {
  const { t } = useI18n();

  return (
    <main className="section" data-testid="menu-page">
      <p className="eyebrow text-center mb-1">{t.menu.eyebrow}</p>
      <h1 className="page-title font-script">{t.menu.title}</h1>

      <p className="text-center text-[color:var(--ink-soft)] max-w-2xl mx-auto -mt-4 mb-12 italic">
        Selena &amp; Mario · 18 de Juliol de 2026 · El Mirador de les Caves
      </p>

      <div className="max-w-3xl mx-auto grid gap-5">
        {MOMENTS.map((m) => <Moment key={m.id} m={m} />)}
      </div>

      <p className="text-center text-[color:var(--ink-soft)] italic text-sm mt-12 max-w-xl mx-auto" data-testid="menu-diet-note">
        Si tens alguna al·lèrgia o intolerància, ens ho pots dir a l'apartat d'Assistència i ho adaptarem sense problema.
      </p>
    </main>
  );
}
